<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Award, BookOpen, CalendarDays, Droplets, Flower2, ShieldCheck } from '@lucide/vue'
import PageHeader from '../components/PageHeader.vue'
import BadgeAlbum from '../components/BadgeAlbum.vue'
import BadgeCelebration from '../components/BadgeCelebration.vue'
import { api, getAchievements } from '../api'
import { effectsEnabled } from '../feedback'
import { isEnglish } from '../i18n'

const data = ref(null)
const achievements = ref(null)
const albumOpen = ref(false)
const celebrationQueue = ref([])
const route = useRoute(), showCelebration = ref(false)
const labels = computed(() => isEnglish.value ? ['A seed is planted','The first sprout','New leaves appear','A bud is forming','Today, it blooms'] : ['播下一颗种子','第一片嫩芽','长出新的叶子','花苞正在形成','今天，花开了'])
const l = computed(() => isEnglish.value ? {celebrate:'Your garden is watered for today',celebrateDetail:'Every careful recall helps memory take root.',title:'See your growth every day',subtitle:'Complete your daily core plan to water this chamomile plant.',loading:'Walking into the garden…',stage:'Growth stage',stageText:'A plant blooms after seven waterings, then moves permanently into your flower field. Your next check-in plants a new seed.',water:'total waterings',streak:'day streak',passes:'rest passes this month',badges:'Growth badges',bloomed:'bloomed',album:'View collection',field:'Flower field',fieldCount:'blooms kept forever',fieldEmpty:'Your first bloom will be kept here.',nextSeed:'This bloom is now in your flower field. Your next check-in will plant a new seed.',empty:'Complete today’s plan to unlock your first growth badge.'}:{celebrate:'今天的花园已经浇水',celebrateDetail:'每一次认真回忆，都在让记忆扎根。',title:'每一天，都看得见成长',subtitle:'完成每日核心任务，就为这株洋甘菊浇一次水。',loading:'正在走进花园…',stage:'成长阶段',stageText:'每株植物浇水 7 次后开花，并永久移入花田；下一次打卡会种下新的一株。',water:'累计浇水',streak:'连续天数',passes:'本月保护卡',badges:'成长徽章',bloomed:'已绽放',album:'查看图鉴',field:'我的花田',fieldCount:'朵花永久留存',fieldEmpty:'完成连续浇水后，第一朵花会留在这里。',nextSeed:'这株花已移入花田；下次打卡会种下新的一株。',empty:'完成今天的任务，解锁第一枚成长徽章。'})
const stage = computed(() => Math.min(4, data.value?.current_plant?.stage ?? data.value?.garden?.stage ?? 0))
const achievementLabels = { first_day: '第一次浇水', streak_7: '连续七天', streak_30: '月度坚持', reviews_100: '百次回忆' }

const allBadges = computed(() => achievements.value?.badges || [])
const unlockedBadges = computed(() => allBadges.value.filter(b => b.unlocked))
const recentBadges = computed(() => [...unlockedBadges.value]
  .sort((a, b) => String(b.unlocked_at || '').localeCompare(String(a.unlocked_at || '')))
  .slice(0, 3))

/* 同一会话内同一批 newly_unlocked 只庆祝一次（GET 兜底校验可能重复返回） */
const CELE_KEY = 'wortgarten-badges-celebrated'
function filterUncelebrated(codes) {
  try {
    const seen = new Set(JSON.parse(sessionStorage.getItem(CELE_KEY) || '[]'))
    const fresh = codes.filter(c => !seen.has(c))
    if (fresh.length) sessionStorage.setItem(CELE_KEY, JSON.stringify([...seen, ...fresh]))
    return fresh
  } catch { return codes }
}

onMounted(async () => {
  data.value = await api('/api/garden')
  try {
    achievements.value = await getAchievements()
    const fresh = filterUncelebrated(achievements.value.newly_unlocked || [])
    if (fresh.length) {
      const byCode = new Map(allBadges.value.map(b => [b.code, b]))
      celebrationQueue.value = fresh.map(code => byCode.get(code) || { code, name: code, rarity: 'copper' })
    }
  } catch { achievements.value = null }
  if (route.query.celebrate === '1' && effectsEnabled()) { showCelebration.value = true; window.setTimeout(() => { showCelebration.value = false }, 3200) }
})
</script>
<template>
  <div>
    <Transition name="celebration"><div v-if="showCelebration" class="garden-celebration"><img src="/garden-stage-5.png" :alt="l.celebrate"/><div><p class="eyebrow">HEUTE GESCHAFFT</p><h2>{{l.celebrate}}</h2><span>{{l.celebrateDetail}}</span></div></div></Transition>
    <PageHeader eyebrow="MEIN GARTEN" :title="l.title" :subtitle="l.subtitle" />
    <div v-if="!data" class="loading-card">{{l.loading}}</div>
    <template v-else>
      <section class="garden-hero">
        <div class="art-matte garden-art"><img :src="`/garden-stage-${stage+1}.png`" :alt="labels[stage]" /></div>
        <div class="garden-copy"><p class="eyebrow">{{l.stage}} {{ stage+1 }}/5</p><h2>{{ labels[stage] }}</h2><p>{{l.stageText}}</p><div class="stage-dots"><span v-for="n in 5" :key="n" :class="{ active: n <= stage+1 }"></span></div></div>
      </section>
      <section class="garden-stats">
        <div><Droplets /><span><b>{{ data.garden.water_count || 0 }}</b><small>{{l.water}}</small></span></div>
        <div><CalendarDays /><span><b>{{ data.streak.current_streak || 0 }}</b><small>{{l.streak}}</small></span></div>
        <div><ShieldCheck /><span><b>{{ Math.max(0,2-(data.streak.rest_passes_used || 0)) }}</b><small>{{l.passes}}</small></span></div>
      </section>
      <section class="garden-field section-card">
        <div class="section-title"><div><p class="eyebrow">FLOWER FIELD</p><h2>{{ l.field }}</h2></div><Flower2 /></div>
        <p class="field-copy"><b>{{ data.field?.count || 0 }}</b> {{ l.fieldCount }}</p>
        <p v-if="data.current_plant?.status === 'bloomed'" class="field-note">{{ l.nextSeed }}</p>
        <div v-if="data.field?.plants?.length" class="field-plants">
          <div v-for="plant in data.field.plants.slice(0, 6)" :key="plant.id" class="field-plant">
            <img src="/garden-stage-5.png" :alt="l.bloomed" />
            <small>#{{ plant.sequence }}</small>
          </div>
        </div>
        <p v-else class="field-empty">{{ l.fieldEmpty }}</p>
      </section>
      <section class="section-card">
        <div class="section-title"><div><p class="eyebrow">ACHIEVEMENTS</p><h2>{{l.badges}}</h2></div><Award /></div>
        <template v-if="achievements">
          <div class="badge-summary">
            <p><b>{{ unlockedBadges.length }}</b><small> / {{ allBadges.length }} {{l.bloomed}}</small></p>
            <button class="badge-album-button" type="button" @click="albumOpen = true"><BookOpen :size="18" />{{l.album}}</button>
          </div>
          <div class="badge-recent">
            <div v-for="item in recentBadges" :key="item.code" :class="`rarity-${item.rarity}`">
              <span class="badge-dot"><Award :size="20" /></span>
              <span class="badge-recent-info"><b>{{ item.name }}</b><small>{{ (item.unlocked_at || '').slice(0, 10) }}</small></span>
            </div>
            <p v-if="!recentBadges.length" class="empty-state">{{l.empty}}</p>
          </div>
        </template>
        <template v-else>
          <div class="achievement-grid">
            <div v-for="item in data.achievements || []" :key="item.code"><span><Award /></span><b>{{ achievementLabels[item.code] || item.code }}</b><small>{{ (item.unlocked_at || '').slice(0,10) }}</small></div>
            <p v-if="!(data.achievements || []).length" class="empty-state">{{l.empty}}</p>
          </div>
        </template>
      </section>
    </template>
    <BadgeAlbum :open="albumOpen" :collections="achievements?.collections || []" :badges="allBadges" @close="albumOpen = false" />
    <BadgeCelebration v-if="celebrationQueue.length" :badges="celebrationQueue" @done="celebrationQueue = []" />
  </div>
</template>

<style scoped>
/* —— 花园主视觉：中玻璃面板 + 单列横幅（720px 壳密度规范） —— */
.garden-hero {
  grid-template-columns: 1fr;
  background: var(--glass-bg);
  -webkit-backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-highlight), var(--glass-shadow);
}

/* 洋甘菊 cream 底 PNG：套 .art-matte 画板罩（暗色下呈"裱起的画"） */
.garden-art {
  margin:14px 14px 0;
  overflow:hidden;
  /* Do not inherit the generic art-matte's pale 6px frame here: it reads as
     an accidental white export seam against this full-width garden image. */
  border:0;
  border-radius:22px;
  background:var(--paper-soft);
}
.garden-hero .garden-art img {
  display: block;
  width:100%;
  height:clamp(220px,32vw,280px);
  object-fit:cover;
  /* Keep the original panoramic crop, but trim the source export gutter
     inside the matte so no white seam is visible at either edge. */
  /* The source PNG itself includes a wider left export gutter.  Crop it
     inside the existing panoramic frame rather than showing a false border. */
  transform:scale(1.08);
  transform-origin:center;
}
/* 覆盖层曾直接给 img 加暗色描边；画板罩容器已承担边框，去除内框避免双重框 */
[data-theme="dark"] .garden-art img { border: 0; }

.garden-copy { padding:20px 22px 24px; }

.garden-field { margin-top:20px; }
.field-copy { color:var(--muted); margin-top:-4px; }
.field-copy b { color:var(--sage-dark); font:700 28px var(--font-display); }
.field-note,.field-empty { margin-top:12px; color:var(--muted); line-height:1.55; font-size:13px; }
.field-plants { display:flex; flex-wrap:wrap; gap:10px; margin-top:16px; }
.field-plant { position:relative; width:58px; height:58px; overflow:hidden; border-radius:16px; background:var(--paper-soft); border:1px solid var(--glass-border); }
.field-plant img { width:100%; height:100%; object-fit:cover; object-position:center 42%; }
.field-plant small { position:absolute; right:4px; bottom:3px; padding:1px 4px; border-radius:6px; background:rgba(255,253,248,.86); color:var(--sage-dark); font-size:9px; font-weight:700; }

/* —— 三枚统计砖：免 blur 玻璃 inset（轨道行纪律），拉开间距 —— */
.garden-stats { gap: 10px; overflow: visible; }
.garden-stats > div {
  min-height: 72px;
  padding: 14px 10px;
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-md);
  background: var(--glass-bg-inset);
}

/* —— 徽章墙：桌面视口同样双列；玻璃砖 + 奖章底色暗色适配 —— */
@media (min-width: 901px) {
  .achievement-grid { grid-template-columns: 1fr 1fr; }
}
.achievement-grid > div {
  background: var(--glass-bg-inset);
  border: 1px solid var(--glass-border);
}
[data-theme="dark"] .achievement-grid > div span {
  background: rgba(216, 178, 98, .16);
  color: var(--gold);
}

/* —— 徽章区升级：完成度摘要 + 最近绽放 + 图鉴入口 —— */
.badge-summary { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.badge-summary p { display: flex; align-items: baseline; gap: 2px; }
.badge-summary b { font: 700 24px var(--font-display); color: var(--gold); }
.badge-summary small { color: var(--muted); }
.badge-album-button { display: inline-flex; align-items: center; gap: 8px; min-height: 44px; padding: 0 16px; border: 1px solid var(--glass-border); border-radius: var(--radius-full); background: var(--glass-bg-inset); color: var(--sage-dark); font-weight: 700; box-shadow: var(--glass-highlight); }
.badge-album-button:hover { background: var(--glass-bg-soft); }
.badge-recent { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
@media (max-width: 600px) { .badge-recent { grid-template-columns: 1fr; } }
.badge-recent > div { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: var(--radius-md); background: var(--glass-bg-inset); border: 1px solid var(--glass-border); }
.badge-recent .badge-dot { flex: none; width: 40px; height: 40px; border-radius: 50%; display: grid; place-items: center; background: color-mix(in srgb, var(--gold) 20%, transparent); color: var(--gold); }
.badge-recent > div.rarity-copper .badge-dot { background: color-mix(in srgb, var(--terracotta) 20%, transparent); color: var(--terracotta); }
.badge-recent > div.rarity-silver .badge-dot { background: color-mix(in srgb, var(--muted) 18%, transparent); color: var(--muted); }
.badge-recent > div.rarity-legend .badge-dot { box-shadow: 0 0 0 2px var(--sage-glow); }
.badge-recent-info { display: grid; min-width: 0; }
.badge-recent-info b { font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.badge-recent-info small { color: var(--muted); font-size: 11px; }
.badge-recent .empty-state { grid-column: 1 / -1; }

/* —— 庆祝浮层：文案卡重玻璃化；写死的浅色幕布补暗色 —— */
.garden-celebration > div {
  background: var(--glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%);
  backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%);
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-highlight), var(--glass-shadow);
}
[data-theme="dark"] .garden-celebration { background: rgba(22, 27, 23, .86); }
</style>
